<template>
  <Teleport to="body">
    <div id="ai-copilot" class="popup">
      <div class="body popup-body" v-on-click-outside="onClickOutsideHandler" style="width: 80%; top: 8rem">
        <div style="display: flex">
          <Sidebar
            v-show="historyOpen"
            :chatList="chatList"
            :setCurrentChat="setCurrentChat"
            :deleteChat="deleteChat"
            v-model="searchQuery"
            :loading="loadingBackground"
          />

          <div class="chat-main">
            <div class="chat-header">
              <div style="display: flex; gap: 5px">
                <button
                  class="btn-icon tooltiped"
                  style="font-size: 1.5em; margin-top: 0.5rem"
                  @click="historyOpen = !historyOpen"
                >
                  <span class="tooltiptext" style="width: max-content">Chat History</span>
                  <font-awesome-icon icon="bars" />
                </button>
                <div class="tooltiped" style="display: flex; align-items: center; gap: 5px; margin-top: 0.5rem">
                  <ToggleSwitch
                    v-if="isAdmin"
                    :state="toggleAnnotatedChat"
                    @change="setuptoggleAnnotatedChat"
                    :size="1"
                  >
                  </ToggleSwitch>
                  <span class="tooltiptext" style="width: max-content">Toggle annotated chat</span>
                </div>
              </div>
              <div style="display: flex; gap: 5px">
                <TitleEditor
                  :text_value="chat_name"
                  @update="updateChatName"
                  @change="chat_name = $event"
                ></TitleEditor>
              </div>
              <div>
                <button class="btn-icon tooltiped" style="font-size: 1.5em; margin-top: 0.5rem" @click="newChat">
                  <span class="tooltiptext" style="width: max-content">New chat</span>
                  +
                </button>
              </div>
            </div>
            <div class="chat-window">
              <div class="message-list">
                <AiMessage :message="initialMessage" :index="0" :isAdmin="isAnnotating" />
                <template v-for="(message, index) in aiMessages" :key="index + 1">
                  <AiMessage
                    :message="message"
                    :index="index"
                    :isAdmin="isAnnotating"
                    :rating="getRating(message.id)"
                    @deleteMessage="deleteMessage"
                    @editMessage="saveAnnotation"
                    @rate="rateMessage"
                    @regen="regenerateMessage"
                  />
                </template>
                <AiMessage
                  v-if="errorOccured"
                  :isError="true"
                  :message="errorMessage"
                  :index="0"
                  :isAdmin="false"
                  @regen="regenerateMessage"
                />
                <div v-if="loading || loadingStreaming" class="message ai-message">
                  <div class="message-content">
                    <img alt="Data loading" src="@/assets/img/snake_logo_animation_200.gif" style="width: 5rem" />
                  </div>
                </div>
              </div>
            </div>
            <div class="input-area">
              <textarea
                ref="userInputArea"
                v-model="userInput"
                placeholder="Type a message..."
                class="message-input"
                rows="2"
              ></textarea>
              <button @click="abort = true" class="abort-btn" v-if="loadingStreaming">
                <font-awesome-icon icon="stop" class="black-icon" />
              </button>
              <button @click="sendMessage" class="send-btn" v-if="!(loading || loadingStreaming)">Send</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from "uuid";
import { useMagicKeys, whenever } from "@vueuse/core";
import { vOnClickOutside } from "@vueuse/components";
import { ref, nextTick, watch, computed, onMounted } from "vue";
import { useTextareaAutosize } from "@vueuse/core";
import { useRoute } from "vue-router";
import * as smd from "streaming-markdown";
import { useAppContextStore } from "@/store/app_context";
import { userSessionStore } from "@/store/user_session";

import ToggleSwitch from "./ToggleSwitch.vue";

import TitleEditor from "./TitleEditor.vue";
import Sidebar from "./AiCopilot/Sidebar.vue";
import AiMessage from "./AiCopilot/AiMessage.vue";

import { ChatMessage, ChatPayload, Chat, Rating } from "@/types/copilot";
import { serieContextStore } from "@/store/serie_context";
import { ApiMessage } from "@/ems/WSClient";
import { PClient } from "@/ems/PClient";

interface StreamElement {
  element: HTMLElement;
  parser: any;
  renderer: any;
}
interface Props {
  initialMessage?: string;
}
const props = defineProps<Props>();
const emit = defineEmits(["close"]);
const toggleAnnotatedChat = ref(false);
const errorOccured = ref(false);
const onClickOutsideHandler: [(evt: any) => void, {}] = [
  (evt: any) => {
    emit("close");
  },
  { ignore: ["[class*=vc]", "[class*=popup-body]"] },
];
const keys = useMagicKeys({
  passive: false,
});
whenever(keys.Escape, () => {
  emit("close");
});

whenever(keys["Control+Enter"], () => {
  sendMessage();
});
const initialMessage: ChatMessage = {
  id: "sys",
  role: "system",
  content:
    "Hello!<br />I'm EMS Copilot, your AI assistant with a PhD-level medical knowledge and expertise in machine learning and statistics. I'm here to help with medical queries, explain complex concepts, and assist with data analysis. How can I assist you today?",
};
const errorMessage: ChatMessage = {
  id: "sys",
  role: "system",
  content: "An error occurred while processing your request. Please try again.",
};

const route = useRoute();
const app_context = useAppContextStore();
const serieContext = serieContextStore();
const user_session = userSessionStore();
const rootApi = app_context.remote_automl_url();
const loading = ref<boolean>(false);
const loadingBackground = ref<boolean>(false);
const loadingStreaming = ref<boolean>(false);
const messages = ref<ApiMessage[]>([]);
const local_client = new PClient(loading, messages);
const clientBackground = new PClient(loadingBackground, messages);

const chat_id = ref<number>();
const chat_name = ref<string>("Copilot Chat session");
const aiMessages = ref<ChatMessage[]>([]);
const currentChat = ref<Chat>();
const searchQuery = ref<string>("");

const { textarea: userInputArea, input: userInput } = useTextareaAutosize();

const abort = ref(false);
const historyOpen = ref<boolean>(false);

const sendMessage = async () => {
  if (userInput.value.trim() !== "") {
    const message = userInput.value;
    const chatMessage: ChatMessage = { role: "user", content: message, id: uuidv4() };
    aiMessages.value.push(chatMessage);
    const context = { id: chatMessage.id, value: { serie: serieContext.serie, url: route.path } };

    userInput.value = "";
    const payload: ChatPayload = {
      messages: aiMessages.value,
      owner: user_session.user,
      name: chat_name.value,
      context: context,
    };

    try {
      if (chat_id.value) {
        // existing chat
        currentChat.value = await local_client.queryWs<Chat>(
          "PUT",
          `automl/copilot/chat/${chat_id.value}`,
          null,
          payload
        );
      } else {
        // new chat
        currentChat.value = await local_client.queryWs<Chat>("POST", "automl/copilot/chat", null, payload);
        chat_id.value = currentChat.value.chat_id;
      }
      await streamResponse();
    } catch (error) {
      errorOccured.value = true;
      loading.value = false;
    }
  }
};
interface ChatStream extends ChatMessage {
  chunk?: string;
}
const createMessageIfNotExists = async (event: ChatStream) => {
  const found = aiMessages.value.findIndex((x) => x.id == event.id);
  if (found != -1) {
    return;
  }
  if (Object.keys(event).includes("content")) {
    aiMessages.value.push(event);
    return;
  }
  const message = { ...event };
  delete message.chunk;
  message.content = "";
  aiMessages.value.push(message);
  await nextTick();
  const element = document.getElementById(`message-${event.id}`);
  if (!element) {
    console.error("Element not found ", `message-${event.id}`);
    return;
  }
  element.innerHTML = "";
  const renderer = smd.default_renderer(element);
  const parser = smd.parser(renderer);
  return { element: element, parser: parser, renderer: renderer };
};

const renderMessage = (event: ChatStream, streamElement: StreamElement | undefined) => {
  if (event.content && streamElement?.parser) {
    smd.parser_end(streamElement.parser);
    streamElement = undefined;
    return;
  }
  if (!streamElement) {
    return;
  }
  const found = aiMessages.value.findIndex((x) => x.id == event.id);
  if (found == -1) {
    console.log("error message not found");
    return;
  }
  aiMessages.value[found].content = (aiMessages.value[found]?.content ?? "") + (event?.chunk ?? "");
  smd.parser_write(streamElement.parser, event.chunk ?? "");
};
const streamResponse = async () => {
  loadingStreaming.value = true;
  errorOccured.value = false;
  const token = user_session.token;
  const eventSource = new EventSource(`${rootApi}/automl/copilot/chat/${chat_id.value}/stream?token=${token}`);
  let streamElement: StreamElement | undefined;
  eventSource.onmessage = async (event) => {
    try {
      if (abort.value) {
        eventSource.close();
        abort.value = false;
        loadingStreaming.value = false;
        return;
      }
      const parsedEvent = JSON.parse(event.data);
      const resp = await createMessageIfNotExists(parsedEvent);
      if (resp?.element) {
        streamElement = resp;
      }
      if (parsedEvent?.error) {
        errorOccured.value = true;
        return;
      }
      renderMessage(parsedEvent, streamElement);
    } catch (error) {
      console.error("Failed to parse event:", error);
    }
  };
  eventSource.onerror = async () => {
    eventSource.close();
    if (streamElement && streamElement.parser) {
      smd.parser_end(streamElement.parser);
    }
    await getChatList();
    const foundChat = chatList.value.find((chat) => chat.chat_id === chat_id.value);
    if (foundChat) {
      setCurrentChat(foundChat);
    }
    loadingStreaming.value = false;
  };
};

const updateChatName = async (name: string) => {
  chat_name.value = name;
  if (chat_id.value) {
    const payload: ChatPayload = { name: chat_name.value };
    currentChat.value = await local_client.queryWs<Chat>("PUT", `automl/copilot/chat/${chat_id.value}`, null, payload);
  }
};
const newChat = async () => {
  abort.value = true;
  chat_id.value = undefined;
  chat_name.value = "Copilot Chat session";
  aiMessages.value = [];
};
const deleteChat = async (chat_id: number) => {
  await local_client.queryWs<Chat>("DELETE", `automl/copilot/chat/${chat_id}`);
  await getChatList();
};
const getChatList = async (query: string = "") => {
  if (isAdmin.value) {
    // return all chats for all users if admin
    chatList.value = await clientBackground.queryWs<Chat[]>("GET", `automl/copilot/chat/list/admin?&query=${query}`);
  } else {
    chatList.value = await clientBackground.queryWs<Chat[]>("GET", `automl/copilot/chat/list?query=${query}`);
  }
};
const setCurrentChat = async (chat: Chat) => {
  toggleAnnotatedChat.value = false;
  currentChat.value = chat;
  chat_id.value = chat.chat_id;
  chat_name.value = chat.name;
  if (toggleAnnotatedChat.value) {
    aiMessages.value = chat.annotated_messages;
  } else {
    aiMessages.value = chat.messages;
  }
};

const chatList = ref<Chat[]>([]);
onMounted(async () => {
  userInputArea.value?.focus();
  if (props.initialMessage) {
    userInput.value = props.initialMessage;
  }
  getChatList();
});
const setupAnnotatedChat = () => {
  if (currentChat.value?.messages && !currentChat.value?.annotated_messages?.length) {
    currentChat.value.annotated_messages = currentChat.value.messages;
  }
};

const setuptoggleAnnotatedChat = (state: boolean) => {
  toggleAnnotatedChat.value = state;
  if (state) {
    setupAnnotatedChat();
    aiMessages.value = currentChat.value?.annotated_messages || [];
  } else {
    aiMessages.value = currentChat.value?.messages || [];
  }
};
const saveAnnotation = async (message: ChatMessage) => {
  setupAnnotatedChat();
  const found = aiMessages.value.findLastIndex((x) => x.id == message.id);
  if (found == -1) {
    console.log("error message not found");
    return;
  }
  aiMessages.value[found] = message;
  const payload: ChatPayload = {
    annotated_messages: aiMessages.value,
  };
  currentChat.value = await local_client.queryWs<Chat>("PUT", `automl/copilot/chat/${chat_id.value}`, null, payload);
  aiMessages.value = currentChat.value?.annotated_messages || [];
  getChatList();
};
const getRating = (id: string) => {
  return (currentChat.value?.ratings ?? []).find((x) => x.id == id && x.user_id == user_session.logged_user.UserId);
};
const rateMessage = async (rating: Rating) => {
  const payload: ChatPayload = {
    rating: rating,
  };
  currentChat.value = await local_client.queryWs<Chat>("PUT", `automl/copilot/chat/${chat_id.value}`, null, payload);
  if (toggleAnnotatedChat.value) {
    setupAnnotatedChat();
    aiMessages.value = currentChat.value?.annotated_messages || [];
  } else {
    aiMessages.value = currentChat.value?.messages || [];
  }
  getChatList();
};
const regenerateMessage = async (id: string) => {
  if (loading.value) {
    abort.value = true;
    while (abort.value || loading.value) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }
  let found = aiMessages.value.findIndex((x) => x.id == id);
  if (id == "") {
    found = aiMessages.value.length;
  }
  if (found == -1) {
    console.log("error message not found");
    return;
  }
  while (found > 0 && ["assistant", "tool"].includes(aiMessages.value[found - 1].role)) {
    found--;
  }
  const newMessages = aiMessages.value.slice(0, found);
  aiMessages.value = newMessages;

  const payload: ChatPayload = {
    annotated_messages: [],
    messages: newMessages,
  };
  loading.value = true;
  try {
    currentChat.value = await local_client.queryWs<Chat>("PUT", `automl/copilot/chat/${chat_id.value}`, null, payload);
    await streamResponse();
  } catch (error) {
    errorOccured.value = true;
    loading.value = false;
  }
};
const deleteMessage = async (id: string) => {
  setupAnnotatedChat();
  toggleAnnotatedChat.value = true;
  aiMessages.value = currentChat.value?.annotated_messages || [];
  aiMessages.value = aiMessages.value.filter((x) => x.id != id);
  const payload: ChatPayload = {
    annotated_messages: aiMessages.value,
  };
  currentChat.value = await local_client.queryWs<Chat>("PUT", `automl/copilot/chat/${chat_id.value}`, null, payload);
  aiMessages.value = currentChat.value?.annotated_messages || [];
  getChatList();
};
const isAdmin = computed(() => {
  return !!user_session.logged_user.is_admin;
});
const isAnnotating = computed(() => {
  return isAdmin.value && toggleAnnotatedChat.value;
});
watch(searchQuery, async () => {
  await getChatList(searchQuery.value);
});
</script>

<style lang="css" scoped>
.popup {
  position: fixed;
  top: -50px;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  text-align: center;
  -webkit-transition: opacity 400ms ease-in;
  -moz-transition: opacity 400ms ease-in;
  transition: opacity 400ms ease-in;
}

.popup > div {
  position: relative;
  margin: 0 auto;
  border: 1px solid white;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 34px 0 rgba(0, 0, 0, 0.24);
  pointer-events: initial;
  transition: all 0.3s ease;
}

.body-popup {
  display: flex;
  justify-content: center;
  min-width: 420px;
  transition: all 0.3s ease;
}
.chat-app {
  display: flex;
  flex-direction: column;
  width: 40%;
  height: calc(100vh - 17.5rem);
}
.chat-main {
  height: calc(100vh - 12rem);
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.chat-header {
  margin: auto;
  display: flex;
  justify-content: space-between;
  margin-left: 1rem;
  margin-right: 1rem;
  height: 2.5em;
}
.chat-window {
  flex: 1;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-right: 0;
  margin-left: 0;
  margin-bottom: 10px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.message-list {
  display: flex;
  flex-direction: column;
}

.input-area {
  display: flex;
  justify-content: space-between;
  padding: 10px 20px;
  padding-right: 0;
  padding-left: 0;
  margin-left: 10px;
  border-top: 1px solid #ddd;
}

.message-input {
  width: 90%;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-size: 16px;
  resize: none;
  overflow: auto;
  scrollbar-width: none;
  height: auto;
  max-height: 20vh;
}

.send-btn {
  background-color: var(--green-main-color);
  color: white;
  border: none;
  padding: 10px 15px;
  margin-right: 5px;
  border-radius: 10px;
  font-size: 18px;
  cursor: pointer;
  height: auto;
  align-self: flex-start;
}
.send-btn:hover {
  background-color: var(--green-medium-color);
}
.abort-btn {
  background-color: var(--red-danger-color);
  color: white;
  border: none;
  padding: 10px 15px;
  margin-right: 5px;
  margin-left: 5px;
  border-radius: 10px;
  font-size: 18px;
  cursor: pointer;
  height: auto;
  align-self: flex-start;
}
.abort-btn:hover {
  background-color: #c85e5c;
}
#ai-copilot ::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

#ai-copilot ::-webkit-scrollbar-thumb {
  border-radius: 2px;
  background-color: #0000004d;
}
#ai-copilot ::-webkit-scrollbar-corner,
#ai-copilot ::-webkit-scrollbar-track {
  background-color: #e2e2e2;
}
</style>
